import { Reducer } from 'redux'
import { createSelector, OutputSelector } from 'reselect'
import { ICarrier, ICarrierState, ICarrierResponsePayload, TCarrierStatus } from 'types'
import {
  CARRIER_ACTION_TYPES,
  CARRIER_UPDATE_ACTION_TYPES,
  FMCSA_ACTION_TYPES,
  CARRIER_STATUS_MODAL,
  CARRIER_EDIT_STATUS_ACTION_TYPES,
  EDIT_CONTACT_INSTRUCTIONS_ACTION_TYPE,
  CONTACT_INSTRUCTIONS_MODAL,
  REQUEST_CARRIER_APPROVAL_ACTION_TYPES,
  CARRIER_CREATE_VENDOR_ACTION_TYPES,
  CARRIER_UPDATE_VENDOR_ACTION_TYPES,
  EDIT_CARRIER_TASK_AUTOMATION_CONFIG_ACTION_TYPES,
} from 'utils/actions/carrier'
import { ELD_TRACKING_ACTION_TYPES as eld } from 'apps/carrier/utils/actions/carrierInfo'
import { IStore } from 'apps/carrier/utils/reducers'
import { findStatus, setIcon, getBounceRate } from 'utils/carriers'
import { DEFAULT_CARRIER_SCORE, carrierStatuses } from 'constants/carriers'

const defaultState: ICarrierState = {
  isFetching: false,
  statusModal: false,
  contactInstructionsModal: false,
  contactInstructionsSaving: false,
  actionInProgress: false,
  isFetchingEldTracking: false,
  eldTrackingError: '',
  taskAutomationConfigError: false,
}

const { CARRIER_FAILURE, CARRIER_REQUEST, CARRIER_SUCCESS } = CARRIER_ACTION_TYPES
const {
  CARRIER_UPDATE_FAILURE,
  CARRIER_UPDATE_REQUEST,
  CARRIER_UPDATE_SUCCESS,
} = CARRIER_UPDATE_ACTION_TYPES
const { FMCSA_SUCCESS } = FMCSA_ACTION_TYPES
const { CARRIER_STATUS_MODAL_CLOSE, CARRIER_STATUS_MODAL_OPEN } = CARRIER_STATUS_MODAL
const {
  CARRIER_EDIT_STATUS_FAILURE,
  CARRIER_EDIT_STATUS_REQUEST,
  CARRIER_EDIT_STATUS_SUCCESS,
} = CARRIER_EDIT_STATUS_ACTION_TYPES
const {
  EDIT_CONTACT_INSTRUCTIONS_FAILURE,
  EDIT_CONTACT_INSTRUCTIONS_REQUEST,
  EDIT_CONTACT_INSTRUCTIONS_SUCCESS,
} = EDIT_CONTACT_INSTRUCTIONS_ACTION_TYPE
const {
  CONTACT_INSTRUCTIONS_MODAL_CLOSE,
  CONTACT_INSTRUCTIONS_MODAL_OPEN,
} = CONTACT_INSTRUCTIONS_MODAL

const {
  REQUEST_CARRIER_APPROVAL_FAILURE,
  REQUEST_CARRIER_APPROVAL_REQUEST,
  REQUEST_CARRIER_APPROVAL_SUCCESS,
} = REQUEST_CARRIER_APPROVAL_ACTION_TYPES
const {
  CARRIER_CREATE_VENDOR_FAILURE,
  CARRIER_CREATE_VENDOR_REQUEST,
  CARRIER_CREATE_VENDOR_SUCCESS,
} = CARRIER_CREATE_VENDOR_ACTION_TYPES
const {
  CARRIER_UPDATE_VENDOR_FAILURE,
  CARRIER_UPDATE_VENDOR_REQUEST,
  CARRIER_UPDATE_VENDOR_SUCCESS,
} = CARRIER_UPDATE_VENDOR_ACTION_TYPES

const {
  EDIT_CARRIER_TASK_AUTOMATION_CONFIG_FAILURE,
  EDIT_CARRIER_TASK_AUTOMATION_CONFIG_REQUEST,
  EDIT_CARRIER_TASK_AUTOMATION_CONFIG_SUCCESS,
} = EDIT_CARRIER_TASK_AUTOMATION_CONFIG_ACTION_TYPES

interface ActionType {
  type: string
  error?: string
  payload: any
}

// @ts-ignore
const carrierMetaReducer: Reducer<ICarrierState, ActionType> = (state = defaultState, action) => {
  switch (action.type) {
    case CARRIER_REQUEST:
      return {
        ...state,
        isFetching: true,
        error: undefined,
      }
    case CARRIER_SUCCESS: {
      const {
        payload: { carrierApplication: carrierDetails },
      } = action
      return {
        ...state,
        isFetching: false,
        error: undefined,
        carrierDetails: {
          ...carrierDetails,
          carrierScore: carrierDetails?.carrierScore
            ? {
                ...carrierDetails.carrierScore,
                bounceRate: getBounceRate(carrierDetails.carrierScore.notBouncePct),
              }
            : {
                ...DEFAULT_CARRIER_SCORE,
              },
        },
      }
    }
    case CARRIER_FAILURE: {
      const { error } = action
      return {
        ...state,
        isFetching: false,
        error,
      }
    }
    case CARRIER_UPDATE_REQUEST:
      return {
        ...state,
        isFetching: true,
        error: undefined,
      }
    case CARRIER_UPDATE_SUCCESS: {
      const {
        payload: { carrierApplication: carrierDetails },
      } = action
      return {
        ...state,
        isFetching: false,
        error: undefined,
        carrierDetails: {
          ...carrierDetails,
          carrierScore: carrierDetails.carrierScore
            ? {
                ...carrierDetails.carrierScore,
                bounceRate: getBounceRate(carrierDetails.carrierScore.notBouncePct),
              }
            : {
                ...DEFAULT_CARRIER_SCORE,
              },
        },
      }
    }
    case CARRIER_UPDATE_FAILURE: {
      const { error } = action
      return {
        ...state,
        isFetching: false,
        error,
      }
    }

    case eld.UPDATE_ELD_TRACKING_REQUEST: {
      return {
        ...state,
        isFetchingEldTracking: true,
      }
    }

    case eld.UPDATE_ELD_TRACKING_SUCCESS: {
      return {
        ...state,
        isFetchingEldTracking: false,
        eldTrackingSuccess: true,
        carrierDetails: {
          ...state.carrierDetails,
          ...(action.payload as ICarrierResponsePayload),
        },
      }
    }

    case eld.UPDATE_ELD_TRACKING_FAILURE: {
      return {
        ...state,
        isFetchingEldTracking: false,
        eldTrackingError: action.error,
      }
    }

    case eld.GET_ELD_TRACKING_REQUEST: {
      return {
        ...state,
        isFetchingEldTracking: true,
      }
    }

    case eld.GET_ELD_TRACKING_SUCCESS: {
      return {
        ...state,
        isFetchingEldTracking: false,
        carrierDetails: {
          ...state.carrierDetails,
          ...(action.payload as ICarrierResponsePayload),
        },
      }
    }

    case eld.GET_ELD_TRACKING_FAILURE: {
      return {
        ...state,
        isFetchingEldTracking: false,
        eldTrackingError: action.error,
      }
    }

    case eld.CLEAR_ELD_TRACKING: {
      return {
        ...state,
        eldTrackingError: '',
        eldTrackingSuccess: undefined,
      }
    }

    case FMCSA_SUCCESS: {
      const fmcsaInfo = action.payload as ICarrierResponsePayload
      const { carrierDetails } = state
      const status = findStatus(fmcsaInfo, carrierDetails!) as TCarrierStatus
      const icon = setIcon(status)
      return {
        ...state,
        carrierDetails: {
          ...state.carrierDetails,
          status,
          icon,
        },
      }
    }
    case CARRIER_STATUS_MODAL_OPEN: {
      return {
        ...state,
        error: undefined,
        statusModal: true,
      }
    }
    case CARRIER_STATUS_MODAL_CLOSE: {
      return {
        ...state,
        statusModal: false,
      }
    }
    case CARRIER_EDIT_STATUS_REQUEST: {
      return {
        ...state,
        isFetching: true,
      }
    }
    case CARRIER_EDIT_STATUS_SUCCESS: {
      const carrierState = action.payload.state
      const status = carrierStatuses[carrierState as TCarrierStatus]
      const icon = setIcon(status as TCarrierStatus)
      return {
        ...state,
        isFetching: false,
        statusModal: false,
        carrierDetails: {
          ...state.carrierDetails,
          status,
          icon,
        },
      }
    }
    case CARRIER_EDIT_STATUS_FAILURE: {
      return {
        ...state,
        isFetching: false,
        error: action.error,
      }
    }
    case EDIT_CONTACT_INSTRUCTIONS_REQUEST: {
      return {
        ...state,
        contactInstructionsSaving: true,
        contactInstructionsError: undefined,
      }
    }
    case EDIT_CONTACT_INSTRUCTIONS_FAILURE: {
      const { error } = action
      return {
        ...state,
        contactInstructionsError: error,
        contactInstructionsSaving: false,
      }
    }
    case EDIT_CONTACT_INSTRUCTIONS_SUCCESS: {
      const {
        payload: { bulkEmailEnabled, carrierBulkEmailRecipients, carrierSpecificInfo },
      } = action
      return {
        ...state,
        contactInstructionsModal: false,
        contactInstructionsSaving: false,
        carrierDetails: {
          ...state.carrierDetails,
          carrierSpecificInfo,
          bulkEmailEnabled,
          carrierBulkEmailRecipients,
        },
      }
    }
    case CONTACT_INSTRUCTIONS_MODAL_OPEN: {
      return {
        ...state,
        contactInstructionsModal: true,
      }
    }
    case CONTACT_INSTRUCTIONS_MODAL_CLOSE: {
      return {
        ...state,
        contactInstructionsModal: false,
      }
    }
    case REQUEST_CARRIER_APPROVAL_REQUEST:
    case CARRIER_CREATE_VENDOR_REQUEST:
    case CARRIER_UPDATE_VENDOR_REQUEST: {
      return {
        ...state,
        actionInProgress: true,
      }
    }
    case CARRIER_CREATE_VENDOR_SUCCESS:
    case CARRIER_UPDATE_VENDOR_SUCCESS: {
      return {
        ...state,
        actionInProgress: false,
        carrierDetails: {
          ...state.carrierDetails,
          netsuiteId: action.payload.carrierApplication.netsuiteId,
        },
      }
    }
    case REQUEST_CARRIER_APPROVAL_SUCCESS:
    case REQUEST_CARRIER_APPROVAL_FAILURE:
    case CARRIER_CREATE_VENDOR_FAILURE:
    case CARRIER_UPDATE_VENDOR_FAILURE: {
      return {
        ...state,
        actionInProgress: false,
      }
    }
    case EDIT_CARRIER_TASK_AUTOMATION_CONFIG_REQUEST: {
      return {
        ...state,
        actionInProgress: true,
        taskAutomationConfigError: false,
      }
    }

    case EDIT_CARRIER_TASK_AUTOMATION_CONFIG_SUCCESS: {
      const {
        payload: { canAutomateCheckEta, canAutomateCheckin, canAutomateCheckout },
      } = action

      return {
        ...state,
        actionInProgress: false,
        carrierDetails: {
          ...state.carrierDetails,
          taskAutomationConfig: {
            canAutomateCheckEta,
            canAutomateCheckin,
            canAutomateCheckout,
          },
        },
        taskAutomationConfigError: false,
      }
    }

    case EDIT_CARRIER_TASK_AUTOMATION_CONFIG_FAILURE: {
      return {
        ...state,
        actionInProgress: false,
        taskAutomationConfigError: true,
      }
    }

    default:
      return state
  }
}

export default carrierMetaReducer

/** Selectors */
const selectCarrier = (state: IStore) => state.carrier

interface ISelectCarrierApplication
  extends OutputSelector<
    IStore,
    ICarrier | undefined,
    (res: ICarrierState) => ICarrier | undefined
  > {}
export const selectCarrierDetails: ISelectCarrierApplication = createSelector(
  selectCarrier,
  carrier => carrier?.carrierDetails
)

export const isFetchingEldTracking = createSelector(
  selectCarrier,
  carrier => carrier?.isFetchingEldTracking
)

export const selectEldTrackingError = createSelector(
  selectCarrier,
  carrier => carrier?.eldTrackingError
)

export const isEldTrackingSubmitSuccess = createSelector(
  selectCarrier,
  carrier => carrier?.eldTrackingSuccess
)

export const selectEldTracking = (state: IStore) => {
  const {
    eldTrackingMethod,
    isEldApproved,
    isEldApprovedUpdatedAt,
    isEldApprovedUpdatedById,
    keepTruckinEnabled,
    keepTruckinEnabledUpdatedAt,
  } = state.carrier.carrierDetails || {}

  return {
    isEldApproved,
    isEldApprovedUpdatedAt,
    isEldApprovedUpdatedById,
    eldTrackingMethod,
    keepTruckinEnabledUpdatedAt,
    keepTruckinEnabled,
  }
}

export const selectCarrierBulkEmailInformation = (state: IStore) => {
  const { carrierSpecificInfo = '', bulkEmailEnabled = false, carrierBulkEmailRecipients = [] } =
    state.carrier.carrierDetails || {}

  return {
    carrierSpecificInfo,
    bulkEmailEnabled,
    carrierBulkEmailRecipients,
  }
}
